/* eslint-disable upright/no-primer-text */
import { Text as PrimerText, TextProps } from '@primer/react';
import { customTheme } from '@upright/ui/common/generic/theme/custom-theme';
import { TextSize } from './types';

/* CUSTOM COMPONENT FOR BODY TEXT ON THE PLATFORM

DIRECTIONS
1. HTML element: Renders a <span> by default. If you want render a <p>, use the as prop.

2. FONT SIZE: You can also provide a fontSize: secondary (i.e. small)/micro/large to deviate from the default.
   In general, use default for most text and secondary for supporting text.

3. FONT WEIGHT: Font weights are available directly through Primer's theme object: light, normal, semibold and bold.
   Example: <TextPlatform fontWeight="semibold">...</TextPlatform>

4. COLOR: by default, the default foreground color is rendered.
   If you need to override this, only pass a text or foreground color from the theme (at the moment of writing, our theme does not include a text color key but can be added).
   Example of gray text: <Text sx={{ color: 'fg.muted' }}>

5. RESPONSIVENESS: by design, the font size doesn't scale based on device width.
   If you need responsive body text, use responsive props by passing an array to the sx prop.
   Example: <Text as="p" fontSize={[0, 1]}>...</Text>
   The fontSize=([0, 1]) fetches font sizes for before and after the first breakpoint. Breakpoints and fontSizes are found in customTheme.
*/

type UprightTextProps = React.PropsWithChildren<
  Omit<TextProps, 'fontSize'> & {
    fontSize?: TextSize;
  }
>;

export const Text = (props: UprightTextProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { children, ...rest } = props;
  const mappedFontSize = handleTextPlatformSize({ fontSize: props.fontSize });
  return (
    <PrimerText {...rest} fontSize={mappedFontSize}>
      {children}
    </PrimerText>
  );
};

export function handleTextPlatformSize({ fontSize = 'default' }: { fontSize?: TextSize }) {
  switch (fontSize) {
    case 'default':
      return customTheme.fontSizes[1]; // rem equivalent to 14px. Use for all body text – as well as most controls, like text boxes, dropdowns, buttons, and menus.
    case 'secondary':
      return customTheme.fontSizes[0]; // rem equivalent to 12px. Use for less important and supporting details.
    case 'micro':
      return '0.67rem'; // 10px at 15px root. For special cases where secondary (i.e. small) isn't small enough.
    case 'large':
      return customTheme.fontSizes[2]; // rem equivalent to 16px. For special cases where the default isn't large enough.
    default:
      // Will not execute
      throw Error('Font size out of scope');
  }
}
